import { Box, Typography, Container, Avatar, Chip, Card, CardContent, Grid, Button, Divider, CircularProgress, Alert, Snackbar, LinearProgress, IconButton, Collapse } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { useReferralStats } from '../hooks/useReferralStats';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import DiscordIcon from '@mui/icons-material/SportsEsports';
import HistoryIcon from '@mui/icons-material/History';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LogoutIcon from '@mui/icons-material/Logout';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BookIcon from '@mui/icons-material/Book';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { createStripePortalSession } from '../utils/billingUtils';

const authenticatedPageSx = {
  py: { xs: 5, md: 7 },
  minHeight: 'calc(100vh - 120px)',
  '& .MuiCard-root': {
    background: (theme) => `${theme.palette.background.paper} !important`,
    backgroundImage: 'none !important',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '24px !important',
    boxShadow: (theme) =>
      theme.palette.mode === 'dark'
        ? '0 24px 80px rgba(0, 0, 0, 0.24)'
        : '0 24px 80px rgba(82, 107, 170, 0.12)',
  },
  '& .MuiChip-root': {
    borderRadius: '999px',
  },
  '& .MuiLinearProgress-root': {
    backgroundColor: (theme) =>
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.08) !important'
        : 'rgba(10,13,20,0.08) !important',
  },
};

const chapters = [
  { id: 'binary-search', title: 'Binary Search' },
  { id: 'sorted-list', title: 'SortedList' },
  { id: 'backtracking', title: 'Backtracking' },
  { id: 'linked-list', title: 'Linked Lists' },
  { id: 'interval', title: 'Interval' },
  { id: 'greedy', title: 'Greedy' },
  { id: 'divide-and-conquer', title: 'Divide and Conquer' },
  { id: 'palindrome', title: 'Palindrome' },
  { id: 'trie', title: 'Trie' },
  { id: 'prefix-sum', title: 'Prefix Sum' },
  { id: 'parenthesis', title: 'Parenthesis' },
  { id: 'segment-tree', title: 'Segment Tree' },
  { id: 'monotonic-stack', title: 'Monotonic Stack' },
  { id: 'heap', title: 'Heap' },
  { id: 'two-pointer', title: 'Two Pointer' },
  { id: 'sliding-window', title: 'Sliding Window' },
  { id: 'tree', title: 'Tree' },
  { id: 'arrays-and-hashing', title: 'Arrays & Hashing' },
  { id: 'graph', title: 'Graph' },
  { id: 'dynamic-programming', title: 'Dynamic Programming' },
];

function Profile() {
  const { user, signOut } = useAuth();
  const { hasActiveSubscription, hasLifetimeAccess } = useSubscriptionStatus();
  const { referralCode, referralUrl, stats: referralStats } = useReferralStats();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  const [topicProgressOpen, setTopicProgressOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyReferralLink = () => {
    if (referralUrl) {
      navigator.clipboard.writeText(referralUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };
  
  const navigate = useNavigate();

  // Get completion data from localStorage (same as Course component)
  const [completedChapters] = useState(() => {
    try {
      const saved = localStorage.getItem('completedCourseChapters');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Progress statistics for chapters
  const progressStats = React.useMemo(() => {
    const total = chapters.length;
    const completed = Object.values(completedChapters).filter(Boolean).length;
    // By topic (chapter)
    const topicStats = chapters.reduce((acc, chapter) => {
      acc[chapter.id] = {
        title: chapter.title,
        total: 1,
        completed: completedChapters[chapter.id] ? 1 : 0
      };
      return acc;
    }, {});
    return { total, completed, topicStats };
  }, [completedChapters]);

  useEffect(() => {
    if (!user) {
      navigate('/signin');
    }
  }, [user, navigate]);

  const handleManageSubscription = async () => {
    try {
      setLoading(true);
      const result = await createStripePortalSession(user);
      
      if (!result.success) {
        setError('Failed to open customer portal. Please try again later.');
        //setError(result.error);
        setShowError(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setShowError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const getMembershipColor = () => {
    if (hasLifetimeAccess) return 'warning.main';
    if (hasActiveSubscription) return 'secondary.main';
    return 'text.disabled';
  };

  const getMembershipIcon = () => {
    if (hasLifetimeAccess) return <DiamondIcon />;
    if (hasActiveSubscription) return <StarIcon />;
    return <PersonIcon />;
  };

  return (
    <>
      <Container maxWidth="lg" sx={authenticatedPageSx}>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        {/* Header Section */}
        <motion.div variants={fadeInUp}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Account" size="small" sx={{ mb: 2, bgcolor: 'action.hover', color: 'text.secondary', fontWeight: 700 }} />
            <Typography 
              variant="h3" 
              sx={{ 
                color: 'text.primary',
                fontWeight: 850,
                mb: 2,
                letterSpacing: 0,
              }}
            >
              Your prep dashboard.
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4 }}>
              Manage your account, membership, referrals, and course progress.
            </Typography>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Profile Card */}
          <Grid item xs={12} md={6}>
            <motion.div variants={fadeInUp}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar 
                      sx={{ 
                        width: 64, 
                        height: 64, 
                        bgcolor: getMembershipColor(),
                        mr: 2
                      }}
                    >
                      {getMembershipIcon()}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {user?.email?.split('@')[0] || 'User'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={hasActiveSubscription ? 'Premium User' : 'Free User'}
                      sx={{ 
                        bgcolor: hasActiveSubscription ? 'action.hover' : 'transparent',
                        color: hasActiveSubscription ? 'secondary.main' : 'text.secondary',
                        fontWeight: 'bold'
                      }}
                    />
                    {hasLifetimeAccess && (
                      <Chip 
                        label="Lifetime Access"
                        sx={{ 
                          bgcolor: 'rgba(255, 215, 0, 0.2)',
                          color: '#b7791f',
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Membership Details */}
          <Grid item xs={12} md={6}>
            <motion.div variants={fadeInUp}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ color: 'text.primary', mb: 3, fontWeight: 800 }}>
                    Membership Status
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
                      <strong>Plan:</strong> {hasLifetimeAccess ? 'Lifetime Access' : (hasActiveSubscription ? 'Premium Subscription' : 'Free Plan')}
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.primary', mb: 1 }}>
                      <strong>Status:</strong> {hasActiveSubscription ? 'Active' : 'Inactive'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {hasLifetimeAccess 
                        ? 'You have unlimited access to all premium features forever!'
                        : hasActiveSubscription 
                        ? 'Enjoy access to all premium features and content.'
                        : 'Upgrade to premium for full access to all features.'
                      }
                    </Typography>
                  </Box>

                  {!hasActiveSubscription && (
                    <Button
                      variant="contained"
                      sx={{
                        background: '#0a0d14',
                        color: '#fff',
                        fontWeight: 'bold',
                        '&:hover': {
                          background: '#05070c',
                        }
                      }}
                      onClick={() => navigate('/purchase')}
                    >
                      Upgrade to Premium
                    </Button>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Discord Access */}
          {hasActiveSubscription && process.env.REACT_APP_DISCORD_LINK && (
            <Grid item xs={12}>
              <motion.div variants={fadeInUp}>
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DiscordIcon sx={{ color: 'secondary.main', fontSize: 40, mr: 2 }} />
                        <Box>
                          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
                            Join Our Discord Community
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Connect with other premium members and get exclusive support
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        variant="contained"
                        href={process.env.REACT_APP_DISCORD_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontWeight: 800,
                        }}
                      >
                        Join Discord
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          )}

          {/* Billing Information Card - Only show if has active subscription */}
          {hasActiveSubscription && (
            <Grid item xs={12}>
              <motion.div variants={fadeInUp}>
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <HistoryIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
                          Billing & History
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Access your complete billing history and invoices
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                      View your billing history, download invoices, update payment methods, and manage your subscription through our secure Stripe Customer Portal.
                    </Typography>

                    <Button
                      variant="outlined"
                      startIcon={loading ? <CircularProgress size={20} /> : <CreditCardIcon />}
                      onClick={handleManageSubscription}
                      disabled={loading}
                    >
                      {loading ? 'Opening Portal...' : 'Open Customer Portal'}
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          )}

          {/* Referral Program Card - Show for all logged in users */}
          {user && referralCode && (
            <Grid item xs={12}>
              <motion.div variants={fadeInUp}>
                <Card>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <GroupAddIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                      <Box>
                        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800 }}>
                          Refer Friends & Earn Free Months
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Every 2 successful referrals = 1 free month of premium!
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Referral Link */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                        Your Referral Link
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: 'action.hover',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            color: 'text.primary',
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            fontSize: '0.85rem'
                          }}
                        >
                          {referralUrl}
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
                          onClick={handleCopyReferralLink}
                          sx={{
                            minWidth: 100,
                          }}
                        >
                          {copySuccess ? 'Copied!' : 'Copy'}
                        </Button>
                      </Box>
                    </Box>

                    {/* Stats Grid */}
                    {referralStats && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 850 }}>
                              {referralStats.completedReferrals}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Successful Referrals
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 850 }}>
                              {referralStats.pendingReferrals}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Pending
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 850 }}>
                              {referralStats.monthsAvailable}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Free Months Available
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                            <Typography variant="h4" sx={{ color: 'secondary.main', fontWeight: 850 }}>
                              {referralStats.referralsNeededForNextMonth}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                              Until Next Reward
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    )}

                    {/* Progress Bar */}
                    {referralStats && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Progress to next free month
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 800 }}>
                            {referralStats.progressToNextReward}/2 referrals
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(referralStats.progressToNextReward / 2) * 100}
                          sx={{
                            height: 10,
                            borderRadius: 5,
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: 'success.main',
                              borderRadius: 5
                            }
                          }}
                        />
                      </Box>
                    )}

                    {/* CTA for free users */}
                    {!hasActiveSubscription && referralStats && referralStats.monthsAvailable > 0 && (
                      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255, 215, 0, 0.1)', borderRadius: 2, border: '1px solid rgba(255, 215, 0, 0.3)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CardGiftcardIcon sx={{ color: 'warning.main' }} />
                          <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 800 }}>
                            You have {referralStats.monthsAvailable} free month(s) waiting! Subscribe to claim them.
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          )}

        </Grid>

        {/* Learning Progress Section */}
        <motion.div variants={fadeInUp}>
          <Box sx={{ mt: 6 }}>
            <Card sx={{ overflow: 'visible' }}>
              <CardContent sx={{ p: 4 }}>
                {/* Header with Icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                  <Avatar sx={{ 
                    bgcolor: 'secondary.main', 
                    color: '#fff', 
                    width: 56, 
                    height: 56,
                    mr: 3
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 30 }} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h4" 
                      sx={{ 
                        color: 'text.primary',
                        fontWeight: 850,
                        mb: 1
                      }}
                    >
                      Learning Progress
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        color: 'text.secondary'
                      }}
                    >
                      Track your journey through coding interview mastery
                    </Typography>
                  </Box>
                </Box>

                {/* Total Progress Card */}
                <Card sx={{ mb: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmojiEventsIcon sx={{ color: 'secondary.main', mr: 1 }} />
                        <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                          Overall Progress
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${progressStats.completed} / ${progressStats.total} Complete`}
                        sx={{
                          bgcolor: 'action.hover',
                          color: 'secondary.main',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(progressStats.completed / progressStats.total) * 100}
                      sx={{
                        height: 12,
                        borderRadius: 6,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: 'secondary.main',
                          borderRadius: 6
                        }
                      }}
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        mt: 1,
                        textAlign: 'center'
                      }}
                    >
                      {Math.round((progressStats.completed / progressStats.total) * 100)}% Complete
                    </Typography>
                  </CardContent>
                </Card>

                {/* Topic Progress Section */}
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BookIcon sx={{ color: 'secondary.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, flex: 1 }}>
                        Topic Progress
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setTopicProgressOpen((open) => !open)}
                        sx={{ 
                          color: 'secondary.main',
                          transition: 'transform 0.3s ease',
                          transform: topicProgressOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                        aria-label={topicProgressOpen ? 'Collapse' : 'Expand'}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Box>
                    <Collapse in={topicProgressOpen}>
                      <Grid container spacing={2}>
                        {Object.entries(progressStats.topicStats)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([topicId, stats]) => (
                            <Grid item xs={12} sm={6} md={4} key={topicId}>
                              <Card sx={{ borderRadius: 2, transition: 'transform 0.2s ease', '&:hover': { transform: 'translateY(-2px)' } }}>
                                <CardContent sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }} noWrap>
                                      {stats.title}
                                    </Typography>
                                    <Chip 
                                      size="small"
                                      label={`${stats.completed}/${stats.total}`}
                                      sx={{
                                        bgcolor: 'action.hover',
                                        color: stats.completed === stats.total ? 'success.main' : 'secondary.main',
                                        fontSize: '0.75rem',
                                        fontWeight: 'bold'
                                      }}
                                    />
                                  </Box>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(stats.completed / stats.total) * 100}
                                    sx={{
                                      height: 6,
                                      borderRadius: 3,
                                      '& .MuiLinearProgress-bar': {
                                        backgroundColor: stats.completed === stats.total ? 'success.main' : 'secondary.main',
                                        borderRadius: 3
                                      }
                                    }}
                                  />
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                      </Grid>
                    </Collapse>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </Box>
        </motion.div>

        {/* Sign Out Button - Bottom of Page */}
        <motion.div variants={fadeInUp}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={handleSignOut}
              sx={{
                borderColor: '#c24150',
                color: '#c24150',
                fontWeight: 'bold',
                px: 6,
                py: 2,
                fontSize: '1rem',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#9f2f3d',
                  bgcolor: 'rgba(255, 68, 68, 0.1)',
                  color: '#9f2f3d',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255, 68, 68, 0.2)'
                }
              }}
            >
              Sign Out
            </Button>
          </Box>
        </motion.div>
      </motion.div>
    </Container>

    <Snackbar 
      open={showError} 
      autoHideDuration={6000} 
      onClose={handleCloseError}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseError} 
        severity="error" 
        sx={{ width: '100%' }}
      >
        {error}
      </Alert>
    </Snackbar>
    </>
  );
}

export default Profile; 
